import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import {
	INSTANT_SEARCH_CACHE_SECONDS,
	INSTANT_SEARCH_LOCATION_CITY_FILTER,
	INSTANT_SEARCH_LOCATION_FILTER_MAX_ITEMS,
} from 'src/app/pages/search/shared/consts';
import type { LocationItem } from 'src/app/pages/search/shared/types';

import { useInstantSearchIndexName } from '../../../../../useInstantSearchIndexName';
import { useSearchClient } from '../../../../../useSearchClient';

type Result = ModelHookResult<
	undefined,
	{
		searchForRelevantCities: (selectedLocation: string) => Promise<readonly LocationItem[]>;
	}
>;

export function useSearchForRelevantCities(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		data: { instantSearchIndexName },
	} = useInstantSearchIndexName();
	const { getCountry } = useI18n();

	return {
		actions: {
			searchForRelevantCities: useCallback(
				async (selectedLocation: string) => {
					const response = await typesenseClient
						.collections(instantSearchIndexName)
						.documents()
						.search(
							{
								q: '*',
								query_by: '*',
								facet_by: INSTANT_SEARCH_LOCATION_CITY_FILTER,
								facet_query: `${INSTANT_SEARCH_LOCATION_CITY_FILTER}:`,
								max_facet_values: INSTANT_SEARCH_LOCATION_FILTER_MAX_ITEMS,
							},
							{
								cacheSearchResultsForSeconds: INSTANT_SEARCH_CACHE_SECONDS,
							},
						);
					if (!response.facet_counts?.length) return [];

					const facetCount = response.facet_counts.find(
						(facet) => facet.field_name === INSTANT_SEARCH_LOCATION_CITY_FILTER,
					);

					if (!facetCount?.counts) return [];

					return facetCount.counts.map(({ count, highlighted, value }) => ({
						label: getCountry(value)?.name || value,
						value,
						highlighted,
						count,
						isRefined: selectedLocation === value,
						type: 'city' as const,
					}));
				},
				[getCountry, instantSearchIndexName, typesenseClient],
			),
		},
	};
}
